import { inject } from '@angular/core';
import { Router, CanActivateFn, ActivatedRouteSnapshot } from '@angular/router';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { from, of } from 'rxjs';
import { switchMap, take } from 'rxjs/operators';
import { DemoModeService } from '../services/demo-mode.service';
import { SystemAccessService } from '../services/system-access.service';
import { MembershipService } from '../services/membership.service';
import { resolveStanding } from './standing.util';
import { hasPortalAck } from '../utils/paused-portal-ack.util';

/**
 * Portal route guard: redirects authenticated members to /home, shows portal to everyone else.
 * - Unauthenticated → show Portal (return true)
 * - Authenticated + member → redirect to /home (member's dashboard)
 * - Authenticated + PAUSED member → the access-paused wall, unless they explicitly chose
 *   the portal from it (the acknowledgment marker) — then the portal renders
 * - Authenticated + non-member → show Portal (user can see join options)
 * - ?preview query param → always show Portal (for admin preview)
 * - Demo mode → redirect to /home (demo user is always "authenticated")
 */
export const portalGuard: CanActivateFn = (route: ActivatedRouteSnapshot) => {
  // ?preview bypasses the redirect — allows admins to preview the portal while authenticated
  if (route.queryParams['preview'] !== undefined) {
    return of(true);
  }

  const demoModeService = inject(DemoModeService);
  const router = inject(Router);

  if (demoModeService.isDemo()) {
    return of(router.createUrlTree(['/home']));
  }

  const oidcSecurityService = inject(OidcSecurityService);
  const systemAccessService = inject(SystemAccessService);
  const membershipService = inject(MembershipService);

  return oidcSecurityService.isAuthenticated$.pipe(
    take(1),
    switchMap(({ isAuthenticated }) => {
      if (!isAuthenticated) {
        return of(true); // Show portal to anonymous users
      }

      // Authenticated — check if user is a brand member before redirecting. Platform-root
      // monitors route into the app by NAME (isMember()'s true for them is a representation
      // accident — see the signal's own caveat); the explicit leg survives any future
      // privilege-level cleanup.
      return from(systemAccessService.getPrivilegeLevel()).pipe(
        switchMap(async () => {
          if (systemAccessService.isMember() || systemAccessService.isRoot()) {
            return router.createUrlTree(['/home']);
          }

          // A PAUSED member sees the wall that explains their state ONCE; after the
          // explicit "browse the brand portal" choice the acknowledgment lets them
          // browse the brand's public face without being re-walled on every navigation.
          const standing = await resolveStanding(membershipService);
          if (standing === 'paused') {
            return hasPortalAck() ? true : router.createUrlTree(['/access-paused']);
          }

          // Authenticated but not a member — show portal (join options visible)
          return true;
        })
      );
    })
  );
};
